import React, { useState, useRef } from 'react';
import ReturnButton from './ReturnButton';
import { auth } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from "../firebase";
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { getInitials } from './ProfilePage';
import { db } from '../firebase';
import NationalitySelector from './NationalitySelector';
import AcademicProgramSelector from './AcademicProgramSelector';
import SectorSelector from './SectorSelector';
import ContactPreferenceSelector from './ContactPreferenceSelector';
import ImageCropper from './ImageCropper';
import EmailChangeModal from './EmailChangeModal';

const EditProfile = ({ user, setCurrentUser, setUsers }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);


  // Check if name is valid (contains first and last name)
  const isValidFullName = (name) => {
    if (!name) return false;
    const nameParts = name.trim().split(/\s+/);
    return nameParts.length >= 2;
  };

  const [showCropper, setShowCropper] = useState(false);  // Add this state
  const [tempImageUrl, setTempImageUrl] = useState(null);  // Add this state

  // Form state - Original Fields
  const [displayEmail, setDisplayEmail] = useState(user.displayEmail || user.email || '');
  const [coreEmail, setCoreEmail] = useState(user.email || ''); // core email is not editable
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [imageUrl, setImageUrl] = useState(user.image || '');
  const [imageFile, setImageFile] = useState(null);
  const [name, setName] = useState(user.name || '');
  const [isNameEditable, setIsNameEditable] = useState(!isValidFullName(user.name));
  const [email, setEmail] = useState(user.email || '');
  const [blurb, setBlurb] = useState(user.blurb || '');
  const [bio, setBio] = useState(user.bio || '');
  const [location, setLocation] = useState(user.location || '');
  //const [industry, setIndustry] = useState(user.industry || '');
  const [currentRole, setCurrentRole] = useState(user.currentRole || '');
  const [currentCompany, setCurrentCompany] = useState(user.currentCompany || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [linkedIn, setLinkedIn] = useState(user.linkedIn || '');
  const [gradYear, setGradYear] = useState(user.gradYear || '');
  const [nationalities, setNationalities] = useState(user.nationalities || []);
  const [selectedMajors, setSelectedMajors] = useState(user.majors || []);
  const [selectedMinors, setSelectedMinors] = useState(user.minors || []);
  const [selectedCertificates, setSelectedCertificates] = useState(user.certificates || []);
  const [extracurriculars, setExtracurriculars] = useState(user.extracurriculars || '');
  const [preferredContact, setPreferredContact] = useState(user.preferredContact || []);

  // Form state - New Fields
  //const [currentEmployer, setCurrentEmployer] = useState(user.currentEmployer || '');
  const [nearestHubCity, setNearestHubCity] = useState(user.nearestHubCity || '');
  //const [currentCity, setCurrentCity] = useState(user.currentCity || '');
  const [hometown, setHometown] = useState(user.hometown || '');
  const [sectors, setSectors] = useState(user.sectors || '');
  const [gradDegrees, setGradDegrees] = useState(user.gradDegrees || '');
  // const [previousWork, setPreviousWork] = useState(user.previousWork || '');
  const [manorPositions, setManorPositions] = useState(user.manorPositions || '');
  const [comments, setComments] = useState(user.comments || '');

  // Error states
  const [status, setStatus] = useState('');
  const [roleCompanyError, setRoleCompanyError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [linkedInError, setLinkedInError] = useState('');
  const [gradYearError, setGradYearError] = useState('');

  const validatePhone = (value) => {
    const phoneRegex = /^\+?[\d\s-()]{10,}$/;
    if (value && !phoneRegex.test(value)) {
      setPhoneError('Please enter a valid phone number');
      return false;
    }
    setPhoneError('');
    return true;
  };

  const validateLinkedIn = (value) => {
    const linkedInRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/.*$/;
    if (value && !linkedInRegex.test(value)) {
      setLinkedInError('Please enter a valid LinkedIn URL');
      return false;
    }
    setLinkedInError('');
    return true;
  };

  

  const validateGradYear = (value) => {
    if (value && (value < 1980 || value > 2040)) {
      setGradYearError('Graduation year must be between 1980 and 2040');
      return false;
    }
    setGradYearError('');
    return true;
  };

  // Handle changes
  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('File selected:', file);
      const url = URL.createObjectURL(file);
      console.log('Created URL:', url);
      setTempImageUrl(url);
      setShowCropper(true);
    }
  };

  const handleCropComplete = (blob) => {
    console.log('Crop completed, blob:', blob);
    setImageFile(blob);
    const url = URL.createObjectURL(blob);
    console.log('Setting image URL:', url);
    setImageUrl(url);
    setShowCropper(false);
  };

  // Add an effect to monitor state changes
  React.useEffect(() => {
    console.log('Image state updated:', {
      imageUrl,
      imageFile: imageFile ? 'present' : 'none',
      showCropper,
      tempImageUrl
    });
  }, [imageUrl, imageFile, showCropper, tempImageUrl]);

  // Handle crop cancellation
  const handleCropCancel = () => {
    setShowCropper(false);
    setTempImageUrl(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("Auth state:", auth.currentUser);  // Add this
    console.log("Auth UID:", auth.currentUser?.uid);  // Add this
    console.log("User ID we're updating:", user.id);  // Add this

    // Validate all fields
    
    const isPhoneValid = validatePhone(phone);
    const isLinkedInValid = validateLinkedIn(linkedIn);
    const isGradYearValid = validateGradYear(gradYear);

    if ( !isPhoneValid || !isLinkedInValid || !isGradYearValid) {
      setStatus('Please fix all errors before submitting');
      return;
    }

    setStatus('Updating profile...');

    try {
      let updatedData = {
        name: isNameEditable ? name : user.name,
        email: coreEmail, // core email remains unchanged
        displayEmail, // new display email field
        blurb,
        bio,
        location,
        //industry,
        currentRole,
        currentCompany,
        phone,
        linkedIn,
        nationalities,
        majors: selectedMajors,
        minors: selectedMinors,
        certificates: selectedCertificates,
        gradYear: gradYear ? parseInt(gradYear) : null,
        // New fields
        // currentEmployer,
        nearestHubCity,
        //currentCity,
        hometown,
        sectors,
        gradDegrees,
        //previousWork,
        manorPositions,
        comments,
        extracurriculars,
        lastUpdated: new Date().toISOString()
      };
      console.log("Checking for image file...")
      if (imageFile) {
        console.log("New Image Found, Updating...")
        const imageRef = ref(storage, `profileImages/${user.id}`);
        await uploadBytes(imageRef, imageFile);
        updatedData.image = await getDownloadURL(imageRef);
      }
      console.log("Uploaded?")

      console.log("Current user:", user);
      console.log("Document path:", `users/${user.id}`);
      const userDoc = doc(db, 'users', user.id);
      console.log("Update data:", updatedData);
      await updateDoc(userDoc, updatedData);

      setCurrentUser({ ...user, ...updatedData });
      setUsers(prevUsers => {
        const userExists = prevUsers.some(u => u.id === user.id);
        if (userExists) {
          // Update existing user
          return prevUsers.map(u => u.id === user.id ? { ...u, ...updatedData } : u);
        } else {
          // Add new user
          return [...prevUsers, { ...user, ...updatedData }];
        }
      });

      setStatus('Profile Updated Successfully!');
      setTimeout(() => navigate('/'), 750);
    } catch (error) {
      console.error("Error updating profile: ", error);
      setStatus('Failed to Update!');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white p-8 rounded-lg shadow-md">
        <div className="flex justify-between pt-4">
        <ReturnButton />
            <button
              type="submit"
              form="profileForm" // Add this
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-6 rounded-md transition-colors"
            >
              Save
            </button>
          </div>
          {status && (
          <div className={`my-4 p-3 rounded-md ${
            status === 'Failed to Update!' 
              ? 'bg-red-100 text-red-800 border border-red-200' 
              : 'bg-green-100 text-green-800 border border-green-200'
          }`}>
            {status}
          </div>
        )}
        
        
        <h1 className="text-3xl font-semibold text-center mb-8">Edit Profile</h1>
        
        <form id="profileForm" onSubmit={handleSubmit} className="space-y-6">
          
          {/* Profile Picture Section */}
          <div className="text-center">
            <div 
              onClick={() => fileInputRef.current.click()}
              className="w-32 h-32 mx-auto mb-2 rounded-full overflow-hidden cursor-pointer relative group"
            >
              <Avatar className="w-full h-full">
                <AvatarImage src={imageUrl} />
                <AvatarFallback>{getInitials(name)}</AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-white text-sm">Change Photo</span>
              </div>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              className="hidden"
              accept="image/*"
            />
            {showCropper && (
              <ImageCropper
                imageUrl={tempImageUrl}
                onCropComplete={handleCropComplete}
                onCancel={handleCropCancel}
              />
            )}
          </div>
          <h4 className="text-xl font-semibold">Basic Info</h4>
          {/* Basic Info Section */}
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`border border-gray-300 rounded-md w-full px-3 py-2 ${!isNameEditable ? 'bg-gray-100' : ''}`}
                  disabled={!isNameEditable}
                  required
                />
                {!isNameEditable && (
                  <p className="text-sm text-gray-500 mt-1">Name is no longer editable</p>
                )}
                {isNameEditable && (
                  <p className="text-sm text-gray-500 mt-1">Name can only be set once!</p>
                )}
              </div>
              <div>
                <label htmlFor="coreEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  Account Email
                </label>
                <input
                  type="email"
                  id="coreEmail"
                  value={auth.currentUser.email}
                  className="border border-gray-300 rounded-md w-full px-3 py-2 bg-gray-100"
                  disabled
                />
                <p className="text-sm text-gray-500 mt-1 cursor-pointer underline" onClick={() => setShowEmailModal(true)}>Change email</p>
              </div>
              <div>
                  <label htmlFor="gradYear" className="block text-sm font-medium text-gray-700 mb-1">
                    Graduation Year
                  </label>
                  <input
                    type="number"
                    id="gradYear"
                    value={gradYear}
                    onChange={(e) => {
                      setGradYear(e.target.value);
                      validateGradYear(e.target.value);
                    }}
                    className="border border-gray-300 rounded-md w-full px-3 py-2"
                    placeholder="YYYY"
                  />
                  {gradYearError && (
                    <p className="text-red-500 text-sm mt-1">{gradYearError}</p>
                  )}
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
          <label htmlFor="displayEmail" className="block text-sm font-medium text-gray-700 mb-1">
                Contact Email
              </label>
              <input
                type="email"
                id="displayEmail"
                value={displayEmail}
                onChange={(e) => setDisplayEmail(e.target.value)}
                className="border border-gray-300 rounded-md w-full px-3 py-2"
                placeholder="Email shown on your profile"
              />
              <p className="text-sm text-gray-500 mt-1">This email will be visible to other users</p>
            </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      validatePhone(e.target.value);
                    }}
                    className="border border-gray-300 rounded-md w-full px-3 py-2"
                    placeholder="+1 (123) 456-7890"
                  />
                  {phoneError && (
                    <p className="text-red-500 text-sm mt-1">{phoneError}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="linkedin" className="block text-sm font-medium text-gray-700 mb-1">
                    LinkedIn
                  </label>
                  <input
                    type="url"
                    id="linkedin"
                    value={linkedIn}
                    onChange={(e) => {
                      setLinkedIn(e.target.value);
                      validateLinkedIn(e.target.value);
                    }}
                    className="border border-gray-300 rounded-md w-full px-3 py-2"
                    placeholder="https://linkedin.com/in/username"
                  />
                  {linkedInError && (
                    <p className="text-red-500 text-sm mt-1">{linkedInError}</p>
                  )}
                </div>
            </div>

            
            <h4 className="text-xl font-semibold">Location Info</h4>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Location
                  </label>
                  <input
                    type="text"
                    id="location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="border border-gray-300 rounded-md w-full px-3 py-2"
                    placeholder="City, State or Country"
                  />
                </div>
                <div>
                  <label htmlFor="nearestHubCity" className="block text-sm font-medium text-gray-700 mb-1">
                    Nearest Hub City
                  </label>
                  <input
                    type="text"
                    id="nearestHubCity"
                    value={nearestHubCity}
                    onChange={(e) => setNearestHubCity(e.target.value)}
                    className="border border-gray-300 rounded-md w-full px-3 py-2"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                  <label htmlFor="hometown" className="block text-sm font-medium text-gray-700 mb-1">
                    Hometown
                  </label>
                  <input
                    type="text"
                    id="hometown"
                    value={hometown}
                    onChange={(e) => setHometown(e.target.value)}
                    className="border border-gray-300 rounded-md w-full px-3 py-2"
                  />
                </div>
                <div>
                  <NationalitySelector
                  selectedCodes={nationalities}
                  onChange={setNationalities}
                  />
                </div>
              </div>
            </div>

            <h4 className="text-xl font-semibold">Professional Info</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="blurb" className="block text-sm font-medium text-gray-700 mb-1">
                  Short Blurb
                </label>
                <input
                  type="text"
                  id="blurb"
                  value={blurb}
                  onChange={(e) => setBlurb(e.target.value)}
                  className="border border-gray-300 rounded-md w-full px-3 py-2"
                  placeholder="A brief one-line description about yourself"
                  maxLength={100}
                />
              </div>
              <SectorSelector
                selectedSectors={sectors}
                onChange={setSectors}
              />
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="currentRole" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Role
                  </label>
                  <input
                    type="text"
                    id="currentRole"
                    value={currentRole}
                    onChange={(e) => {
                      setCurrentRole(e.target.value);
                    }}
                    className="border border-gray-300 rounded-md w-full px-3 py-2"
                    placeholder="e.g., SWE"
                  />
                  {gradYearError && (
                    <p className="text-red-500 text-sm mt-1">{gradYearError}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="currentCompany" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Company
                  </label>
                  <input
                    type="text"
                    id="currentCompany"
                    value={currentCompany}
                    onChange={(e) => {
                      setCurrentCompany(e.target.value);
                    }}
                    className="border border-gray-300 rounded-md w-full px-3 py-2"
                    placeholder="e.g., Acme Corp"
                  />
                </div>
              </div>
              {roleCompanyError && (
                <p className="text-red-500 text-sm">{roleCompanyError}</p>
              )}
            </div>
            <h4 className="text-xl font-semibold">Academic Info</h4>
            {/* Academic Information */}
            <AcademicProgramSelector
              selectedMajors={selectedMajors}
              selectedMinors={selectedMinors}
              selectedCertificates={selectedCertificates}
              onMajorsChange={setSelectedMajors}
              onMinorsChange={setSelectedMinors}
              onCertificatesChange={setSelectedCertificates}
            />
            <div>
              <label htmlFor="extracurriculars" className="block text-sm font-medium text-gray-700 mb-1">
                Other Extracurriculars
              </label>
              <textarea
                id="extracurriculars"
                value={extracurriculars}
                onChange={(e) => setExtracurriculars(e.target.value)}
                className="border border-gray-300 rounded-md w-full px-3 py-2"
                rows="2"
                placeholder="List any other extracurricular activities..."
              />
            </div>
            
            <h4 className="text-xl font-semibold">Wayne & Personal Info</h4>
            {/* Additional Information */}
            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
                Hobbies & Other Notes
              </label>
              <textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="border border-gray-300 rounded-md w-full px-3 py-2 min-h-[100px]"
                placeholder="Tell us about yourself..."
                rows="3"
              />
            </div>
            <div className="space-y-4">
              <div>
                <label htmlFor="manorPositions" className="block text-sm font-medium text-gray-700 mb-1">
                  Position(s) in The Manor
                </label>
                <input
                  type="text"
                  id="manorPositions"
                  value={manorPositions}
                  onChange={(e) => setManorPositions(e.target.value)}
                  className="border border-gray-300 rounded-md w-full px-3 py-2"
                />
              </div>

              <div>
                <label htmlFor="comments" className="block text-sm font-medium text-gray-700 mb-1">
                  Favorite Memory(ies) from Wayne
                </label>
                <textarea
                  id="comments"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  className="border border-gray-300 rounded-md w-full px-3 py-2"
                  rows="3"
                />
              </div>

              
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-6 rounded-md transition-colors"
            >
              Save
            </button>
          </div>
        </form>

        {/* Status Message */}
        {status && (
          <div className={`mt-4 p-3 rounded-md ${
            status === 'Failed to Update!' 
              ? 'bg-red-100 text-red-800 border border-red-200' 
              : 'bg-green-100 text-green-800 border border-green-200'
          }`}>
            {status}
          </div>
        )}
      </div>
      <EmailChangeModal 
        isOpen={showEmailModal} 
        onClose={() => setShowEmailModal(false)}
        onSuccess={(newEmail) => {
          setEmail(newEmail);
          setCoreEmail(newEmail);
          setCurrentUser(prev => ({...prev, email: newEmail}));
        }}
        currentEmail={coreEmail}
      />
    </div>
    
  );
};

export default EditProfile;